import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Wind,
  Thermometer,
  Droplets,
  Activity,
  Users,
  Sun,
  CheckCircle,
  ArrowRight,
  Building2,
  GraduationCap,
  Stethoscope,
  Landmark,
  Factory,
  Warehouse,
  BarChart3,
  Zap,
  Shield,
  Clock,
  Wifi,
  Battery,
  AlertTriangle,
  TrendingUp,
  FileCheck,
  HelpCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { HreflangTags } from '@/components/HreflangTags';
import { useLanguage } from '@/i18n/LanguageContext';

// Custom generated images
const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/1a47832e393bf556c80241729e3ba747db65804cc05047f99d47648b46611d5b.png",
  whyMatters: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/c1f4baf81fd095d3997d117bb39a4ce651a64d3eddc53f4df7e662d20357aad0.png",
  parameters: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/2efb15b622bd4691b773f87e6c0ef7dc859cdc310b1999b9510c3becc2191a32.png",
  tektelic: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/d9429f46d37c8fb2c9fca83d000db1bfe4e113b1bc54bc5abb8ffa4235593706.png",
  connectedInventions: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/935883a9e31f8dfea46fe403ad48ded98faa080aab919491cf8aa11e2954f506.png",
  manualVsIot: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/78de980dc1a41c048f313dcd55120c6437775d2c748779cce3295dca40bffdf4.png",
  standards: "https://static.prod-images.emergentagent.com/jobs/b5540dc6-9998-4dd1-aa31-42aaca713952/images/ec48147895d23006610edbcd33f85447c67e718b674f87cfb16a800ec51c6454.png"
};

// FAQ Component - Modern Card Design
const FAQItem = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div 
      className={`bg-white rounded-xl border border-slate-200 overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-teal-300 ${isOpen ? 'shadow-lg border-teal-400' : ''}`}
    >
      <button
        className="w-full p-6 flex items-start gap-4 text-left group"
        onClick={() => setIsOpen(!isOpen)}
        data-testid={`faq-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-colors ${isOpen ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-teal-100 group-hover:text-teal-600'}`}>
          {index}
        </span>
        <div className="flex-grow">
          <span className={`font-semibold text-lg transition-colors ${isOpen ? 'text-teal-600' : 'text-slate-900 group-hover:text-teal-600'}`}>
            {question}
          </span>
        </div>
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-teal-500 rotate-180' : 'bg-slate-100 group-hover:bg-teal-100'}`}>
          <ChevronDown className={`h-5 w-5 transition-colors ${isOpen ? 'text-white' : 'text-slate-400 group-hover:text-teal-600'}`} />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-6 pb-6 pt-0 pl-[4.5rem]">
          <p className="text-slate-600 leading-relaxed">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
};

const IndoorAirQualityPage = () => {
  const { t } = useLanguage();
  
  return (
    <>
      <Helmet>
        <title>{t('iaqPage.metaTitle')}</title>
        <meta name="description" content={t('iaqPage.metaDesc')} />
        <meta name="keywords" content="indoor air quality monitoring, IAQ monitoring, CO2 monitoring, TVOC monitoring, PM2.5 monitoring, smart building air quality, office air quality monitoring, school air quality monitoring, hospital indoor air quality, LoRaWAN air quality sensor, Sigfox air quality sensor, indoor air quality sensors, commercial building air quality, healthy buildings, air quality and ventilation" />
        <link rel="canonical" href="https://0gbaltics.com/indoor-air-quality-monitoring" />
        <meta property="og:title" content={t('iaqPage.metaTitle')} />
        <meta property="og:description" content={t('iaqPage.metaDesc')} />
        <meta property="og:type" content="website" />
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header />

      <main className="pt-20">
        
        {/* ========================================= */}
        {/* SECTION 1: HERO */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="iaq-hero">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('iaqPage.heroBadge')}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  {t('iaqPage.heroTitle')}
                </h1>
                <p className="text-xl text-slate-300 mb-6 leading-relaxed">
                  {t('iaqPage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <Button 
                    size="lg"
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                    onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                    data-testid="hero-cta-discuss"
                  >
                    {t('iaqPage.ctaButton')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
                
                {/* Technology badges */}
                <div className="flex flex-wrap gap-3">
                  {[
                    t('iaqPage.techCO2'),
                    t('iaqPage.techTemperature'),
                    t('iaqPage.techHumidity'),
                    t('iaqPage.techPM25'),
                    t('iaqPage.techTVOC'),
                    t('iaqPage.techOccupancy')
                  ].map((tech, i) => (
                    <span key={i} className="bg-slate-800 border border-slate-700 text-slate-300 text-sm px-3 py-1 rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src={IMAGES.hero}
                  alt="Indoor Air Quality Monitoring - office, school and healthcare spaces with IAQ sensors and cloud dashboard"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 2: WHY IAQ MATTERS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="why-iaq-matters">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('iaqPage.whyBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('iaqPage.whyTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('iaqPage.whyDesc')}
                </p>
                <div className="space-y-3">
                  {[
                    t('iaqPage.outcome1'),
                    t('iaqPage.outcome2'),
                    t('iaqPage.outcome3'),
                    t('iaqPage.outcome4'),
                    t('iaqPage.outcome5'),
                  ].map((outcome, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0" />
                      <span className="text-slate-700">{outcome}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex flex-col items-center">
                <img 
                  src={IMAGES.whyMatters}
                  alt="Why monitor IAQ - health, comfort, ventilation, building efficiency, compliance"
                  className="w-full max-w-md h-auto"
                />
                <div className="mt-8 bg-teal-50 border border-teal-200 p-6 rounded-lg w-full">
                  <h4 className="text-teal-800 font-semibold mb-3">{t('iaqPage.keyDrivers')}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { icon: Activity, label: t('iaqPage.driverHealth') },
                      { icon: Users, label: t('iaqPage.driverComfort') },
                      { icon: Wind, label: t('iaqPage.driverVentilation') },
                      { icon: Building2, label: t('iaqPage.driverEfficiency') },
                      { icon: FileCheck, label: t('iaqPage.driverCompliance') },
                    ].map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-teal-600" />
                          <span className="text-teal-700 text-sm">{item.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 3: WHAT SHOULD BE MEASURED */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="iaq-parameters">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('iaqPage.monitorBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('iaqPage.monitorTitle')}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {[
                {
                  icon: Activity,
                  title: t('iaqPage.co2'),
                  description: t('iaqPage.co2Desc'),
                  color: 'bg-blue-600'
                },
                {
                  icon: Thermometer,
                  title: t('iaqPage.temperature'),
                  description: t('iaqPage.temperatureDesc'),
                  color: 'bg-red-500'
                },
                {
                  icon: Droplets,
                  title: t('iaqPage.humidity'),
                  description: t('iaqPage.humidityDesc'),
                  color: 'bg-cyan-500'
                },
                {
                  icon: Wind,
                  title: t('iaqPage.pm'),
                  description: t('iaqPage.pmDesc'),
                  color: 'bg-amber-500'
                },
                {
                  icon: AlertTriangle,
                  title: t('iaqPage.tvoc'),
                  description: t('iaqPage.tvocDesc'),
                  color: 'bg-purple-500'
                },
              ].map((param, i) => {
                const Icon = param.icon;
                return (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`param-${param.title.split(' ')[0].toLowerCase()}`}>
                    <div className={`${param.color} p-4 flex items-center gap-3`}>
                      <Icon className="h-6 w-6 text-white" />
                      <h3 className="text-white font-semibold">{param.title}</h3>
                    </div>
                    <div className="p-6">
                      <p className="text-slate-600 text-sm leading-relaxed">{param.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center">
              <img 
                src={IMAGES.parameters}
                alt="IAQ parameters - CO2, Temperature, Humidity, PM, TVOC, Occupancy"
                className="w-full max-w-3xl h-auto"
              />
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 4: TEKTELIC DEVICES */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="tektelic-sensors">
          <div className="container-0g">
            {/* Section Header */}
            <div className="max-w-3xl mb-12">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('iaqPage.sensorsBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('iaqPage.sensorsTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('iaqPage.sensorsDesc')}
              </p>
            </div>
            
            {/* Device Cards - 3 columns */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {/* TEKTELIC BREEZE */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden group hover:border-teal-500 transition-colors">
                <div className="aspect-square bg-slate-200 overflow-hidden flex items-center justify-center p-8">
                  <img 
                    src="/assets/images/devices/tektelic-breeze.png"
                    alt="TEKTELIC BREEZE indoor air quality sensor"
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-slate-900 font-semibold text-lg mb-2">TEKTELIC BREEZE</h4>
                  <p className="text-slate-600 text-sm mb-4">
                    {t('iaqPage.tektelicBreezeDesc')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['CO2', 'Temp', 'Humidity', 'LoRaWAN', '5+ years'].map((spec, i) => (
                      <span key={i} className="bg-teal-100 text-teal-700 text-xs px-2 py-1 rounded">{spec}</span>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* TEKTELIC BREEZE-V */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden group hover:border-teal-500 transition-colors">
                <div className="aspect-square bg-slate-200 overflow-hidden flex items-center justify-center p-8">
                  <img 
                    src="/assets/images/devices/tektelic-breeze-v.png"
                    alt="TEKTELIC BREEZE-V indoor air quality sensor with PIR occupancy detection"
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-slate-900 font-semibold text-lg mb-2">TEKTELIC BREEZE-V</h4>
                  <p className="text-slate-600 text-sm mb-4">
                    {t('iaqPage.tektelicBreezeVDesc')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['PIR', 'CO2', 'Motion', 'Smart Room', 'BLE'].map((spec, i) => (
                      <span key={i} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">{spec}</span>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* TEKTELIC BREEZE-D */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden group hover:border-teal-500 transition-colors">
                <div className="aspect-square bg-slate-200 overflow-hidden flex items-center justify-center p-4">
                  <img 
                    src="/assets/images/devices/tektelic-breeze-d.webp"
                    alt="TEKTELIC BREEZE-D CO2 Smart Room Sensor with E-Ink Display"
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-slate-900 font-semibold text-lg mb-2">TEKTELIC BREEZE-D</h4>
                  <p className="text-slate-600 text-sm mb-4">
                    {t('iaqPage.tektelicBreezeDDesc')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['E-Ink Display', 'CO2', 'BLE', 'Wireless', 'Low Power'].map((spec, i) => (
                      <span key={i} className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded">{spec}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Info Boxes */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-teal-50 border border-teal-200 p-6 rounded-lg">
                <h4 className="text-teal-800 font-semibold mb-3">{t('iaqPage.whenTektelic')}</h4>
                <p className="text-teal-700 text-sm mb-3">{t('iaqPage.tektelicRelevant')}</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    t('iaqPage.tektelicBenefit1'),
                    t('iaqPage.tektelicBenefit2'),
                    t('iaqPage.tektelicBenefit3'),
                    t('iaqPage.tektelicBenefit4'),
                    t('iaqPage.tektelicBenefit5'),
                    t('iaqPage.tektelicBenefit6'),
                  ].map((benefit, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-teal-600 flex-shrink-0" />
                      <span className="text-teal-700 text-xs">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-slate-900 p-6 rounded-lg">
                <h4 className="text-white font-semibold mb-4">{t('iaqPage.bestFitTektelic')}</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Building2, label: t('iaqPage.corpOffices') },
                    { icon: GraduationCap, label: t('iaqPage.schoolsUnis') },
                    { icon: Landmark, label: t('iaqPage.publicBuildings') },
                    { icon: Stethoscope, label: t('iaqPage.healthcareFacilities') },
                  ].map((env, i) => {
                    const Icon = env.icon;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-teal-400" />
                        <span className="text-slate-300 text-sm">{env.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 5: CONNECTED INVENTIONS DEVICES */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="connected-inventions-sensors">
          <div className="container-0g">
            {/* Section Header */}
            <div className="max-w-3xl mb-12">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                {t('iaqPage.connectedBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                {t('iaqPage.connectedTitle')}
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                {t('iaqPage.connectedDesc')}
              </p>
            </div>
            
            {/* Device Cards Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {/* AirWits Basic */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden group hover:border-teal-500 transition-colors">
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                  <img 
                    src="/assets/images/devices/airwits-basic.jpg"
                    alt="Connected AirWits temperature and humidity sensor"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-white font-semibold mb-2">Connected AirWits</h4>
                  <p className="text-slate-400 text-sm mb-3">{t('iaqPage.airwitsBasicDesc')}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Temp</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Humidity</span>
                  </div>
                </div>
              </div>
              
              {/* AirWits CO2 */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden group hover:border-teal-500 transition-colors">
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                  <img 
                    src="/assets/images/devices/airwits-co2.jpg"
                    alt="Connected AirWits CO2 sensor with display"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-white font-semibold mb-2">Connected AirWits CO2</h4>
                  <p className="text-slate-400 text-sm mb-3">{t('iaqPage.airwitsCO2Desc')}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">CO2</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Temp</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Humidity</span>
                  </div>
                </div>
              </div>
              
              {/* AirWits CO2 Plus */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden group hover:border-teal-500 transition-colors">
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                  <img 
                    src="/assets/images/devices/airwits-co2-plus.png"
                    alt="Connected CO2 Plus with e-ink display showing air quality levels"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-white font-semibold mb-2">Connected CO2 Plus</h4>
                  <p className="text-slate-400 text-sm mb-3">{t('iaqPage.airwitsCO2PlusDesc')}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">CO2</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Display</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Temp</span>
                  </div>
                </div>
              </div>
              
              {/* AirWits PM */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden group hover:border-teal-500 transition-colors">
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                  <img 
                    src="/assets/images/devices/airwits-pm.jpg"
                    alt="Connected AirWits PM particulate matter sensor"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-white font-semibold mb-2">Connected AirWits PM</h4>
                  <p className="text-slate-400 text-sm mb-3">{t('iaqPage.airwitsPMDesc')}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">PM1.0</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">PM2.5</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">PM10</span>
                  </div>
                </div>
              </div>
              
              {/* Flex Sense */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden group hover:border-teal-500 transition-colors">
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                  <img 
                    src="/assets/images/devices/flex-sense.jpg"
                    alt="Connected Inventions Flex Sense multi-sensor device"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-white font-semibold mb-2">Flex Sense</h4>
                  <p className="text-slate-400 text-sm mb-3">{t('iaqPage.flexSenseDesc')}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Modular</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Multi-Sensor</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Flexible</span>
                  </div>
                </div>
              </div>
              
              {/* AirWits IAQ */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden group hover:border-teal-500 transition-colors">
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                  <img 
                    src="/assets/images/devices/airwits-iaq.jpg"
                    alt="Connected AirWits IAQ TVOC monitoring sensor"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-white font-semibold mb-2">Connected AirWits IAQ</h4>
                  <p className="text-slate-400 text-sm mb-3">{t('iaqPage.airwitsIAQDesc')}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">TVOC</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Temp</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Humidity</span>
                  </div>
                </div>
              </div>
              
              {/* AirWits EcoSense */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden group hover:border-teal-500 transition-colors">
                <div className="aspect-[4/3] bg-slate-200 overflow-hidden">
                  <img 
                    src="/assets/images/devices/airwits-ecosense.png"
                    alt="Connected AirWits EcoSense solar-powered sensor"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h4 className="text-white font-semibold mb-2">Connected AirWits EcoSense</h4>
                  <p className="text-slate-400 text-sm mb-3">{t('iaqPage.airwitsEcoSenseDesc')}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Solar</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Battery-Free</span>
                    <span className="bg-teal-600/20 text-teal-400 text-xs px-2 py-1 rounded">Low Maintenance</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Benefits Box */}
            <div className="bg-slate-800 p-8 rounded-lg border border-slate-700 max-w-4xl mx-auto">
              <h4 className="text-white font-semibold mb-4 text-center">{t('iaqPage.whenConnected')}</h4>
              <p className="text-slate-400 text-sm mb-6 text-center">{t('iaqPage.connectedUseful')}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  t('iaqPage.connectedBenefit1'),
                  t('iaqPage.connectedBenefit2'),
                  t('iaqPage.connectedBenefit3'),
                  t('iaqPage.connectedBenefit4'),
                  t('iaqPage.connectedBenefit5'),
                  t('iaqPage.connectedBenefit6'),
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-400 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 6: WHICH DEVICE FOR WHICH PROJECT */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="device-selection">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('iaqPage.deviceSelectionBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('iaqPage.deviceSelectionTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('iaqPage.deviceSelectionDesc')}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="device-comparison-table">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="px-6 py-4 text-left font-semibold">{t('iaqPage.tableBuilding')}</th>
                    <th className="px-6 py-4 text-left font-semibold">{t('iaqPage.tableNeed')}</th>
                    <th className="px-6 py-4 text-left font-semibold">{t('iaqPage.tableSensor')}</th>
                    <th className="px-6 py-4 text-left font-semibold">{t('iaqPage.tableWhy')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {[
                    {
                      type: t('iaqPage.officePortfolio'),
                      need: t('iaqPage.officeNeed'),
                      sensor: 'TEKTELIC BREEZE / BREEZE-V',
                      why: t('iaqPage.officeWhy')
                    },
                    {
                      type: t('iaqPage.classroomSchool'),
                      need: t('iaqPage.classroomNeed'),
                      sensor: 'TEKTELIC BREEZE or AirWits CO2',
                      why: t('iaqPage.classroomWhy')
                    },
                    {
                      type: t('iaqPage.uniCampus'),
                      need: t('iaqPage.uniNeed'),
                      sensor: t('iaqPage.uniSensor'),
                      why: t('iaqPage.uniWhy')
                    },
                    {
                      type: t('iaqPage.healthcareClinics'),
                      need: t('iaqPage.healthcareNeed'),
                      sensor: t('iaqPage.healthcareIaqSensor'),
                      why: t('iaqPage.healthcareWhy')
                    },
                    {
                      type: t('iaqPage.residentialAreas'),
                      need: t('iaqPage.residentialNeed'),
                      sensor: t('iaqPage.residentialSensor'),
                      why: t('iaqPage.residentialWhy')
                    },
                    {
                      type: t('iaqPage.municipalBuildings'),
                      need: t('iaqPage.municipalNeed'),
                      sensor: 'TEKTELIC BREEZE',
                      why: t('iaqPage.municipalWhy')
                    },
                    {
                      type: t('iaqPage.industrialIndoor'),
                      need: t('iaqPage.industrialNeed'),
                      sensor: t('iaqPage.industrialSensor'),
                      why: t('iaqPage.industrialWhy')
                    },
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-6 py-4 font-medium text-slate-900">{row.type}</td>
                      <td className="px-6 py-4 text-slate-600 text-sm">{row.need}</td>
                      <td className="px-6 py-4 text-teal-600 font-medium text-sm">{row.sensor}</td>
                      <td className="px-6 py-4 text-slate-600 text-sm">{row.why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 7: WHY IOT-BASED IAQ IS BETTER */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="iot-vs-manual">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('iaqPage.iotVsManualBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('iaqPage.iotVsManualTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('iaqPage.iotVsManualDesc')}
                </p>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  {t('iaqPage.iotVsManualDesc2')}
                </p>
                
                <div className="space-y-4">
                  {[
                    { icon: Clock, title: t('iaqPage.continuous247'), desc: t('iaqPage.continuous247Desc') },
                    { icon: AlertTriangle, title: t('iaqPage.realTimeAlerts'), desc: t('iaqPage.realTimeAlertsDesc') },
                    { icon: BarChart3, title: t('iaqPage.historicalTrends'), desc: t('iaqPage.historicalTrendsDesc') },
                    { icon: Building2, title: t('iaqPage.portfolioBenchmark'), desc: t('iaqPage.portfolioBenchmarkDesc') },
                    { icon: Zap, title: t('iaqPage.betterDecisions'), desc: t('iaqPage.betterDecisionsDesc') },
                    { icon: TrendingUp, title: t('iaqPage.scalableOps'), desc: t('iaqPage.scalableOpsDesc') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{item.title}</h4>
                          <p className="text-slate-600 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <img 
                  src={IMAGES.manualVsIot}
                  alt="Manual spot checks vs real-time connected IAQ monitoring comparison"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 8: IAQ, VENTILATION AND ENERGY */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="iaq-energy">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('iaqPage.iaqEnergyBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('iaqPage.iaqEnergyTitle')}
                </h2>
              </div>
              
              <div className="prose prose-lg max-w-none text-slate-600">
                <p className="text-lg leading-relaxed mb-6">
                  {t('iaqPage.iaqEnergyDesc1')}
                </p>
                
                <div className="grid md:grid-cols-2 gap-6 my-8">
                  <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                    <h4 className="text-slate-900 font-semibold mb-3">{t('iaqPage.co2Ventilation')}</h4>
                    <p className="text-slate-600 text-sm">
                      {t('iaqPage.co2VentilationDesc')}
                    </p>
                  </div>
                  <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                    <h4 className="text-slate-900 font-semibold mb-3">{t('iaqPage.tempHumidityInsights')}</h4>
                    <p className="text-slate-600 text-sm">
                      {t('iaqPage.tempHumidityInsightsDesc')}
                    </p>
                  </div>
                </div>

                <p className="leading-relaxed mb-6">
                  {t('iaqPage.iaqEnergyDesc2')}
                </p>
                
                <div className="bg-teal-50 border border-teal-200 p-6 rounded-lg">
                  <p className="text-teal-800 text-sm mb-0">
                    <strong>Note:</strong> {t('iaqPage.iaqEnergyNote')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 9: STANDARDS AND COMPLIANCE */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="iaq-standards">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('iaqPage.standardsBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('iaqPage.standardsTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('iaqPage.standardsDesc')}
                </p>
                
                <div className="space-y-6">
                  <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                    <h4 className="text-white font-semibold mb-2">{t('iaqPage.whoGuidelines')}</h4>
                    <p className="text-slate-400 text-sm">
                      {t('iaqPage.whoGuidelinesDesc')}
                    </p>
                  </div>
                  
                  <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                    <h4 className="text-white font-semibold mb-2">{t('iaqPage.ashraeStandard')}</h4>
                    <p className="text-slate-400 text-sm">
                      {t('iaqPage.ashraeStandardDesc')}
                    </p>
                  </div>
                  
                  <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                    <h4 className="text-white font-semibold mb-2">{t('iaqPage.enStandard')}</h4>
                    <p className="text-slate-400 text-sm">
                      {t('iaqPage.enStandardDesc')}
                    </p>
                  </div>
                </div>
                
                <div className="mt-8 bg-teal-600/20 border border-teal-500/30 p-6 rounded-lg">
                  <p className="text-teal-300 text-sm">
                    <strong>Important:</strong> {t('iaqPage.standardsImportant')}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col items-center">
                <img 
                  src={IMAGES.standards}
                  alt="Standards and compliance frameworks - WHO, ASHRAE, EN 16798"
                  className="w-full max-w-md h-auto mb-8"
                />
                <div className="bg-slate-800 p-6 rounded-lg border border-slate-700 w-full">
                  <h4 className="text-white font-semibold mb-4">{t('iaqPage.iaqHelps')}</h4>
                  <div className="space-y-2">
                    {[
                      t('iaqPage.iaqHelp1'),
                      t('iaqPage.iaqHelp2'),
                      t('iaqPage.iaqHelp3'),
                      t('iaqPage.iaqHelp4'),
                      t('iaqPage.iaqHelp5'),
                      t('iaqPage.iaqHelp6'),
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-teal-400 flex-shrink-0" />
                        <span className="text-slate-300 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 10: USE CASES */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="iaq-use-cases">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('iaqPage.useCasesBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('iaqPage.useCasesTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('iaqPage.useCasesDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: Building2,
                  title: t('iaqPage.ucOffices'),
                  description: t('iaqPage.ucOfficesDesc'),
                  color: 'bg-blue-600'
                },
                {
                  icon: GraduationCap,
                  title: t('iaqPage.ucSchools'),
                  description: t('iaqPage.ucSchoolsDesc'),
                  color: 'bg-green-600'
                },
                {
                  icon: Stethoscope,
                  title: t('iaqPage.ucHealthcare'),
                  description: t('iaqPage.ucHealthcareDesc'),
                  color: 'bg-red-500'
                },
                {
                  icon: Landmark,
                  title: t('iaqPage.ucMunicipal'),
                  description: t('iaqPage.ucMunicipalDesc'),
                  color: 'bg-purple-600'
                },
                {
                  icon: Building2,
                  title: t('iaqPage.ucCommercial'),
                  description: t('iaqPage.ucCommercialDesc'),
                  color: 'bg-amber-500'
                },
                {
                  icon: Warehouse,
                  title: t('iaqPage.ucIndustrial'),
                  description: t('iaqPage.ucIndustrialDesc'),
                  color: 'bg-slate-600'
                },
              ].map((useCase, i) => {
                const Icon = useCase.icon;
                return (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`use-case-${i}`}>
                    <div className={`${useCase.color} p-4 flex items-center gap-3`}>
                      <Icon className="h-6 w-6 text-white" />
                      <h3 className="text-white font-semibold">{useCase.title}</h3>
                    </div>
                    <div className="p-6">
                      <p className="text-slate-600 text-sm leading-relaxed">{useCase.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 11: WHY 0g BALTICS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="why-0g-baltics">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('iaqPage.whyUsBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('iaqPage.whyUsTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('iaqPage.whyUsDesc')}
                </p>
                
                <div className="space-y-4 mb-8">
                  {[
                    { title: t('iaqPage.whyUsFeature1'), desc: t('iaqPage.whyUsFeature1Desc') },
                    { title: t('iaqPage.whyUsFeature2'), desc: t('iaqPage.whyUsFeature2Desc') },
                    { title: t('iaqPage.whyUsFeature3'), desc: t('iaqPage.whyUsFeature3Desc') },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-slate-900">{item.title}</h4>
                        <p className="text-slate-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-slate-900 p-8 rounded-lg">
                  <h4 className="text-white font-semibold mb-6 text-lg">{t('common.relatedSolutions') || 'Related Solutions'}</h4>
                  <div className="space-y-4">
                    {[
                      { to: '/tektelic', label: 'TEKTELIC LoRaWAN', desc: t('common.tektelicDesc') || 'Carrier-grade smart building sensors' },
                      { to: '/connectivity', label: t('common.connectivity') || 'Multi-Network Connectivity', desc: 'LoRaWAN, Sigfox, NB-IoT' },
                      { to: '/iot-for-cities', label: t('common.iotCities') || 'IoT for Smart Cities', desc: t('common.iotCitiesDesc') || 'Municipal deployments' },
                    ].map((link, i) => (
                      <Link key={i} to={link.to} className="block bg-slate-800 p-4 rounded-lg hover:bg-slate-700 transition-colors group">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="text-white font-medium">{link.label}</h5>
                            <p className="text-slate-400 text-sm">{link.desc}</p>
                          </div>
                          <ArrowRight className="h-5 w-5 text-teal-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 12: FAQ */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-gradient-to-b from-slate-50 to-white" data-testid="iaq-faq">
          <div className="container-0g">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4 bg-teal-50 px-4 py-2 rounded-full">
                  {t('iaqPage.faqBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                  {t('iaqPage.faqTitle')}
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  {t('iaqPage.faqDesc')}
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <FAQItem 
                  index={1}
                  question={t('iaqPage.faq1Q')}
                  answer={t('iaqPage.faq1A')}
                />
                <FAQItem 
                  index={2}
                  question={t('iaqPage.faq2Q')}
                  answer={t('iaqPage.faq2A')}
                />
                <FAQItem 
                  index={3}
                  question={t('iaqPage.faq3Q')}
                  answer={t('iaqPage.faq3A')}
                />
                <FAQItem 
                  index={4}
                  question={t('iaqPage.faq4Q')}
                  answer={t('iaqPage.faq4A')}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 13: CTA */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-teal-600" data-testid="iaq-cta">
          <div className="container-0g text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('iaqPage.ctaTitle')}
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg"
                className="bg-white text-teal-700 hover:bg-teal-50 font-semibold"
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="cta-talk-expert"
              >
                {t('iaqPage.ctaButton')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CONTACT FORM */}
        {/* ========================================= */}
        <div id="contact-form">
          <ContactForm />
        </div>

      </main>

      <Footer />
    </>
  );
};

export default IndoorAirQualityPage;
